import React from "react";
import styled from "@emotion/styled";
import { fontWeight, spacing, controlFontSize, layout } from "@atlas-int/tokens";

/**
 * ControlRow -- composable label + input layout for control panels.
 *
 * Wraps the SegmentContainer > LabelContainer + InputContainer pattern
 * from ControlsGrid into a single declarative component.
 *
 * Usage:
 * ```tsx
 * <ControlRow label="Exporter">
 *   <LocationDropdown ... />
 * </ControlRow>
 * ```
 */

/* ─── Styled primitives ──────────────────────────────────────────────────── */

const ControlRowRoot = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  position: relative;
`;

const Label = styled.div<{ $uppercase: boolean }>`
  min-height: ${spacing[12]};
  width: 100%;
  text-align: left;
  font-size: ${controlFontSize.sm};
  text-transform: ${({ $uppercase }) => ($uppercase ? "uppercase" : "none")};
  cursor: default;
  font-weight: ${fontWeight.semibold};
  display: flex;
  align-items: center;
`;

const Input = styled.div`
  height: ${layout.controlsHeight};
`;

/* ─── Props ───────────────────────────────────────────────────────────────── */

export interface ControlRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Label rendered above the input area -- string or ReactNode for inline icons. If omitted, renders a spacer. */
  label?: React.ReactNode;
  /** Whether to uppercase the label. Defaults to true for backward compat. */
  uppercase?: boolean;
  /** Optional override for the input container width */
  inputWidth?: string | number;
  /** Tighter horizontal margin for icon-only controls (arrows, swap buttons). */
  compact?: boolean;
  /** When true, the control grows to fill available space (flex: 1). inputWidth becomes min-width. */
  flex?: boolean;
  /** Maximum width for the control (useful with flex to cap growth). */
  maxWidth?: string | number;
  children: React.ReactNode;
}

/* ─── Component ───────────────────────────────────────────────────────────── */

const ControlRow = React.forwardRef<HTMLDivElement, ControlRowProps>(
  (
    {
      label,
      uppercase = true,
      inputWidth,
      compact = false,
      flex: flexGrow = false,
      maxWidth,
      className,
      children,
      style,
      ...props
    },
    ref,
  ) => {
    const rootStyle: React.CSSProperties = {
      ...style,
      ...(compact
        ? { margin: "0 clamp(-6px, -0.5vw, -2px)" }
        : undefined),
      ...(flexGrow ? { flex: "1 1 auto", minWidth: 0, maxWidth } : undefined),
      ...(!flexGrow && maxWidth ? { maxWidth } : undefined),
    };

    const inputStyle: React.CSSProperties | undefined = flexGrow
      ? { width: "100%", minWidth: inputWidth }
      : inputWidth
        ? { width: inputWidth }
        : undefined;

    return (
      <ControlRowRoot
        ref={ref}
        className={className}
        style={rootStyle}
        {...props}
      >
        {label !== undefined ? (
          <Label $uppercase={uppercase}>{label}</Label>
        ) : (
          <Label $uppercase={uppercase} aria-hidden />
        )}
        <Input style={inputStyle}>{children}</Input>
      </ControlRowRoot>
    );
  },
);

ControlRow.displayName = "ControlRow";

export default ControlRow;
